/**
 * LinkPreview — Fetches and displays OG metadata for URLs in messages
 * Notion-style card design
 */

import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink } from 'lucide-react';

interface OGData {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  favicon?: string;
}

// In-memory cache to avoid re-fetching
const ogCache = new Map<string, OGData | null>();

// Proxy per fetch OG data (usa un CORS proxy leggero)
async function fetchOGData(url: string): Promise<OGData | null> {
  if (ogCache.has(url)) return ogCache.get(url) || null;

  try {
    // Usa allorigins come CORS proxy (gratuito, no API key)
    const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
    const res = await fetch(proxyUrl, { signal: AbortSignal.timeout(5000) });
    if (!res.ok) throw new Error('fetch failed');
    const html = await res.text();

    // Parse OG tags dal HTML
    const getMetaContent = (property: string): string | undefined => {
      const regex = new RegExp(`<meta[^>]*(?:property|name)=["']${property}["'][^>]*content=["']([^"']*)["']`, 'i');
      const altRegex = new RegExp(`<meta[^>]*content=["']([^"']*)["'][^>]*(?:property|name)=["']${property}["']`, 'i');
      return regex.exec(html)?.[1] || altRegex.exec(html)?.[1];
    };

    const title = getMetaContent('og:title') || getMetaContent('twitter:title') || html.match(/<title[^>]*>([^<]+)<\/title>/i)?.[1];
    const description = getMetaContent('og:description') || getMetaContent('twitter:description') || getMetaContent('description');
    const image = getMetaContent('og:image') || getMetaContent('twitter:image');
    const siteName = getMetaContent('og:site_name');

    // Favicon
    const urlObj = new URL(url);
    const favicon = `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`;

    if (!title && !description && !image) {
      ogCache.set(url, null);
      return null;
    }

    const data: OGData = {
      title: title?.trim(),
      description: description?.trim()?.slice(0, 160),
      image: image && (image.startsWith('http') ? image : new URL(image, url).href),
      siteName: siteName?.trim() || urlObj.hostname,
      favicon,
    };
    ogCache.set(url, data);
    return data;
  } catch {
    ogCache.set(url, null);
    return null;
  }
}

interface LinkPreviewProps {
  url: string;
  isOwn: boolean;
}

export function LinkPreview({ url, isOwn }: LinkPreviewProps) {
  const [data, setData] = useState<OGData | null>(ogCache.get(url) || null);
  const [loading, setLoading] = useState(!ogCache.has(url));
  const [error, setError] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (ogCache.has(url)) {
      setData(ogCache.get(url) || null);
      setLoading(false);
      return;
    }

    fetchOGData(url).then(result => {
      if (!mountedRef.current) return;
      if (result) setData(result);
      else setError(true);
      setLoading(false);
    });

    return () => { mountedRef.current = false; };
  }, [url]);

  if (loading || error || !data) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block rounded-lg overflow-hidden mt-1.5 hover:opacity-90 transition-opacity"
      style={{
        border: `1px solid ${isOwn ? 'rgba(255,255,255,0.2)' : 'rgba(55,53,47,0.12)'}`,
        backgroundColor: isOwn ? 'rgba(255,255,255,0.1)' : 'rgba(55,53,47,0.04)',
        maxWidth: 320,
      }}
    >
      {data.image && (
        <img
          src={data.image}
          alt=""
          className="w-full object-cover"
          style={{ maxHeight: 160 }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      <div className="px-3 py-2">
        <div className="flex items-center gap-1.5 mb-1">
          {data.favicon && <img src={data.favicon} alt="" className="w-4 h-4 rounded" />}
          <span className="text-[10px] truncate" style={{ color: isOwn ? 'rgba(255,255,255,0.6)' : '#9B9A97' }}>
            {data.siteName || new URL(url).hostname}
          </span>
          <ExternalLink className="w-3 h-3 flex-shrink-0" style={{ color: isOwn ? 'rgba(255,255,255,0.4)' : '#9B9A97' }} strokeWidth={1.5} />
        </div>
        {data.title && (
          <div className="text-[12px] font-medium leading-snug line-clamp-2" style={{ color: isOwn ? '#fff' : '#37352F' }}>
            {data.title}
          </div>
        )}
        {data.description && (
          <div className="text-[11px] leading-snug mt-0.5 line-clamp-2" style={{ color: isOwn ? 'rgba(255,255,255,0.7)' : '#787774' }}>
            {data.description}
          </div>
        )}
      </div>
    </a>
  );
}
